'use client';

import { Button, Typography } from 'lighthouse';

import { SafeImage } from '@/widgets/SafeImage';

import { getBatterySpecs } from '@/utilities/batteryProduct';
import { rialToToman } from '@/utilities/Financial';
import { GenerateFilePreviewUrl } from '@/utilities/GenerateFilePreviewUrl';
import { withSeparator } from '@/utilities/withSeparator';

import { BATTERY_CHOICE_DEFAULT_BUTTON_TITLE, BATTERY_PRODUCT_HIDDEN_DISCOUNT_PERCENTAGE } from '@/constants/batteryProduct';

import { BatteryChoiceCardProps } from './models';

import { CloseCircle, TickCircle } from 'iconsax-react';

const badgeToneClassNames = {
    primary: 'bg-primary/10 text-primary',
    neutral: 'bg-N100 text-blackHighEmphasis',
    error: 'bg-error/10 text-error'
};

export function BatteryChoiceCard({
    product,
    badge,
    isSuggested = false,
    disabled = false,
    isLoading = false,
    buttonTitle = BATTERY_CHOICE_DEFAULT_BUTTON_TITLE,
    className = '',
    onSelect
}: BatteryChoiceCardProps) {
    const hasDiscount = !!(
        product.discountPercentage &&
        product.sellingPrice &&
        product.discountPercentage !== BATTERY_PRODUCT_HIDDEN_DISCOUNT_PERCENTAGE
    );
    const finalPrice = hasDiscount ? product.sellingPrice : product.price;
    const specs = getBatterySpecs(product);
    const badgeToneClassName = badgeToneClassNames[badge?.tone ?? 'primary'];

    return (
        <article
            className={`flex flex-col gap-2.5 rounded-2xl border border-solid border-N200 bg-white p-3 shadow-elevation1 ${
                product.available ? '' : 'opacity-80'
            } ${className}`}>
            <div className='flex gap-3'>
                <span className='flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-sky-50'>
                    <SafeImage
                        width={56}
                        height={56}
                        src={
                            GenerateFilePreviewUrl({
                                attachableId: product.uuid,
                                attachment: 'PRODUCT',
                                type: 'MAIN',
                                width: 200,
                                height: 200,
                                isPrimary: true,
                                sortOrder: 0
                            }).src
                        }
                        alt={product.title}
                        fallback='/images/battery/battery-placeholder.webp'
                        className='object-contain mix-blend-multiply'
                    />
                </span>

                <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                    <Typography variant='overline' component='span' color='primary' className='font-medium leading-5'>
                        {product.brand.name}
                    </Typography>
                    <Typography variant='subtitle2' component='h3' color='blackHighEmphasis' className='line-clamp-2 font-bold leading-5'>
                        {product.title}
                    </Typography>
                    {!!specs.length && (
                        <Typography variant='overline' component='p' color='blackMediumEmphasis' className='leading-5'>
                            {specs.join(' · ')}
                        </Typography>
                    )}
                </div>
            </div>

            {(!!badge || isSuggested) && (
                <div className='flex flex-wrap items-center gap-2'>
                    {!!badge && (
                        <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${badgeToneClassName}`}>
                            {badge.tone === 'error' ? (
                                <CloseCircle size={12} variant='Bold' color='currentColor' />
                            ) : (
                                <TickCircle size={12} variant={badge.tone === 'neutral' ? 'Linear' : 'Bold'} color='currentColor' />
                            )}
                            {badge.label}
                        </span>
                    )}
                    {isSuggested && (
                        <span className='flex items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-bold text-primary'>
                            <TickCircle size={11} variant='Bold' color='currentColor' />
                            پیشنهاد ازکی‌سرویس
                        </span>
                    )}
                </div>
            )}

            <div className='flex items-center justify-between gap-3 border-0 border-t border-solid border-N100 pt-2.5'>
                {product.available ? (
                    <div className='min-w-0'>
                        {hasDiscount && (
                            <div className='flex items-center gap-1.5'>
                                <span className='rounded-full bg-amber-400 px-2 py-0.5 text-[10.5px] font-extrabold leading-4 text-blackHighEmphasis'>
                                    {product.discountPercentage.toLocaleString('fa-IR')}٪ تخفیف
                                </span>
                                <Typography variant='overline' component='span' color='blackLowEmphasis' className='leading-4 line-through'>
                                    {withSeparator(rialToToman(product.price))} تومان
                                </Typography>
                            </div>
                        )}
                        <Typography variant='h6' component='span' color='primary' fontWeight={800} className='leading-6'>
                            {withSeparator(rialToToman(finalPrice))}
                            <span className='mr-1 text-[11px] font-normal text-blackMediumEmphasis'>تومان</span>
                        </Typography>
                    </div>
                ) : (
                    <span aria-hidden />
                )}
                <Button
                    size='small'
                    variant='contained'
                    color='primary'
                    loading={isLoading}
                    disabled={!product.available || disabled}
                    onClick={() => onSelect(product.uuid)}
                    className='shrink-0 whitespace-nowrap text-white'>
                    {product.available ? buttonTitle : 'ناموجود'}
                </Button>
            </div>
        </article>
    );
}
